import type {
	AiSkillAction,
	AiSkillActionInput,
	AiSkillConnection,
	AiSkillCredentialProfile,
	AiSkillCredentialType,
	AiSkillInputDefinition,
	AiSkillOutputMapping,
	AiSkillSettings,
	AiSkillValueType,
} from '@livechat/shared';
import { type FormEvent, useCallback, useEffect, useMemo, useState } from 'react';

import {
	createTenantAiSkillAction,
	createTenantAiSkillConnection,
	createTenantAiSkillCredential,
	getTenantAiSkills,
	replaceTenantAiSkillCredential,
	setTenantAiSkillActionStatus,
	setTenantAiSkillConnectionStatus,
	setTenantAiSkillCredentialStatus,
	testTenantAiSkillAction,
	updateTenantAiSkillAction,
	updateTenantAiSkillConnection,
} from '../api.js';

interface ConnectionDraft {
	baseOrigin: string;
	description: string;
	id?: string;
	name: string;
	status: 'active' | 'disabled';
}

interface CredentialDraft {
	connectionId: string;
	headerName: string;
	id?: string;
	name: string;
	password: string;
	secret: string;
	type: AiSkillCredentialType;
	username: string;
}

interface ActionDraft extends AiSkillActionInput {
	id?: string;
}

const valueTypes: Array<{ label: string; value: AiSkillValueType }> = [
	{ label: 'Text', value: 'string' },
	{ label: 'Whole number', value: 'integer' },
	{ label: 'Number', value: 'decimal' },
	{ label: 'Yes / no', value: 'boolean' },
	{ label: 'Date', value: 'date' },
	{ label: 'Date and time', value: 'date_time' },
	{ label: 'Choice', value: 'enum' },
];

function blankConnection(): ConnectionDraft {
	return { baseOrigin: '', description: '', name: '', status: 'active' };
}

function blankCredential(connectionId = ''): CredentialDraft {
	return {
		connectionId,
		headerName: 'X-API-Key',
		name: '',
		password: '',
		secret: '',
		type: 'bearer',
		username: '',
	};
}

function blankAction(connectionId = ''): ActionDraft {
	return {
		connectionId,
		description: '',
		inputs: [],
		maxResponseBytes: 65536,
		method: 'GET',
		name: '',
		notFoundStatuses: [404],
		outputs: [],
		path: '/',
		readOnlyAttested: false,
		status: 'draft',
		timeoutMs: 10000,
	};
}

function blankInput(): AiSkillInputDefinition {
	return {
		description: '',
		key: 'reference',
		label: 'Reference',
		location: 'query',
		maxLength: 200,
		required: true,
		target: 'reference',
		type: 'string',
	};
}

function blankOutput(): AiSkillOutputMapping {
	return {
		key: 'status',
		label: 'Status',
		path: 'status',
		type: 'string',
		visibility: 'visitor',
	};
}

function credentialTypeLabel(type: AiSkillCredentialType): string {
	switch (type) {
		case 'none':
			return 'No authentication';
		case 'bearer':
			return 'Bearer token';
		case 'api_key_header':
			return 'API key header';
		case 'basic':
			return 'Basic username/password';
	}
}

export function TenantAiSkills({ tenantId }: { tenantId: string }) {
	const [settings, setSettings] = useState<AiSkillSettings>();
	const [loading, setLoading] = useState(true);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string>();
	const [message, setMessage] = useState<string>();
	const [connectionDraft, setConnectionDraft] = useState<ConnectionDraft>(blankConnection);
	const [credentialDraft, setCredentialDraft] = useState<CredentialDraft>(blankCredential);
	const [actionDraft, setActionDraft] = useState<ActionDraft>(blankAction);
	const [showConnectionForm, setShowConnectionForm] = useState(false);
	const [showCredentialForm, setShowCredentialForm] = useState(false);
	const [showActionForm, setShowActionForm] = useState(false);
	const [testingAction, setTestingAction] = useState<AiSkillAction>();
	const [testValues, setTestValues] = useState<Record<string, string | boolean>>({});
	const [testResult, setTestResult] = useState<{
		durationMs: number;
		errorCode?: string;
		fields?: Record<string, unknown>;
		httpStatus?: number;
		outcome: string;
	}>();

	const load = useCallback(async () => {
		const loaded = await getTenantAiSkills(tenantId);
		setSettings(loaded);
		setCredentialDraft((current) =>
			current.connectionId
				? current
				: { ...current, connectionId: loaded.connections[0]?.id ?? '' },
		);
		setActionDraft((current) =>
			current.connectionId
				? current
				: { ...current, connectionId: loaded.connections[0]?.id ?? '' },
		);
	}, [tenantId]);

	useEffect(() => {
		let current = true;
		setLoading(true);
		void load()
			.catch((caught: unknown) => {
				if (current) {
					setError(caught instanceof Error ? caught.message : 'Could not load Skills');
				}
			})
			.finally(() => {
				if (current) setLoading(false);
			});
		return () => {
			current = false;
		};
	}, [load]);

	const credentialsForAction = useMemo(
		() =>
			settings?.credentials.filter(
				(credential) =>
					credential.connectionId === actionDraft.connectionId &&
					credential.status !== 'archived',
			) ?? [],
		[actionDraft.connectionId, settings?.credentials],
	);

	async function mutate(work: () => Promise<unknown>, success: string): Promise<boolean> {
		if (busy) return false;
		setBusy(true);
		setError(undefined);
		setMessage(undefined);
		try {
			try {
				await work();
			} catch (caught) {
				setError(caught instanceof Error ? caught.message : 'Could not update Skills');
				return false;
			}
			try {
				await load();
				setMessage(success);
			} catch {
				setMessage(`${success} Refresh this page to load the saved changes.`);
			}
			return true;
		} finally {
			setBusy(false);
		}
	}

	function updateConnectionDraft(change: Partial<ConnectionDraft>): void {
		setConnectionDraft((current) => ({ ...current, ...change }));
	}

	function updateActionDraft(change: Partial<ActionDraft>): void {
		setActionDraft((current) => ({ ...current, ...change }));
	}

	function updateCredentialDraft(change: Partial<CredentialDraft>): void {
		setCredentialDraft((current) => ({ ...current, ...change }));
	}

	function updateTestValue(key: string, value: string | boolean): void {
		setTestValues((current) => ({ ...current, [key]: value }));
	}

	async function saveConnection(event: FormEvent<HTMLFormElement>): Promise<void> {
		event.preventDefault();
		const draft = connectionDraft;
		const saved = await mutate(
			() =>
				draft.id
					? updateTenantAiSkillConnection(tenantId, draft.id, draft)
					: createTenantAiSkillConnection(tenantId, draft),
			draft.id ? 'Connection updated.' : 'Connection created.',
		);
		if (!saved) return;
		setConnectionDraft(blankConnection());
		setShowConnectionForm(false);
	}

	async function saveCredential(event: FormEvent<HTMLFormElement>): Promise<void> {
		event.preventDefault();
		const draft = credentialDraft;
		const credential =
			draft.type === 'none'
				? { connectionId: draft.connectionId, name: draft.name, type: draft.type }
				: draft.type === 'bearer'
					? {
							connectionId: draft.connectionId,
							name: draft.name,
							secret: draft.secret,
							type: draft.type,
						}
					: draft.type === 'api_key_header'
						? {
								connectionId: draft.connectionId,
								headerName: draft.headerName,
								name: draft.name,
								secret: draft.secret,
								type: draft.type,
							}
						: {
								connectionId: draft.connectionId,
								name: draft.name,
								password: draft.password,
								type: draft.type,
								username: draft.username,
							};
		const saved = await mutate(
			() =>
				draft.id
					? replaceTenantAiSkillCredential(tenantId, draft.id, credential)
					: createTenantAiSkillCredential(tenantId, credential),
			draft.id ? 'Credential replaced.' : 'Credential created.',
		);
		if (!saved) return;
		setCredentialDraft(blankCredential(settings?.connections[0]?.id));
		setShowCredentialForm(false);
	}

	function editConnection(connection: AiSkillConnection): void {
		setConnectionDraft({
			baseOrigin: connection.baseOrigin,
			description: connection.description,
			id: connection.id,
			name: connection.name,
			status: connection.status === 'active' ? 'active' : 'disabled',
		});
		setShowConnectionForm(true);
	}

	function replaceCredential(profile: AiSkillCredentialProfile): void {
		setCredentialDraft({
			...blankCredential(profile.connectionId),
			headerName: profile.headerName ?? 'X-API-Key',
			id: profile.id,
			name: profile.name,
			type: profile.type,
			username: '',
		});
		setShowCredentialForm(true);
	}

	async function saveAction(event: FormEvent<HTMLFormElement>): Promise<void> {
		event.preventDefault();
		const draft = actionDraft;
		const input: AiSkillActionInput = {
			connectionId: draft.connectionId,
			...(draft.credentialProfileId
				? { credentialProfileId: draft.credentialProfileId }
				: {}),
			description: draft.description,
			inputs: draft.inputs.map((definition) => ({
				...definition,
				...(definition.type === 'enum'
					? {
							enumValues: (definition.enumValues ?? [])
								.map((value) => value.trim())
								.filter(Boolean),
						}
					: { enumValues: undefined }),
			})),
			maxResponseBytes: draft.maxResponseBytes,
			method: draft.method,
			name: draft.name,
			notFoundStatuses: draft.notFoundStatuses,
			outputs: draft.outputs,
			path: draft.path,
			readOnlyAttested: draft.readOnlyAttested,
			status: draft.status,
			timeoutMs: draft.timeoutMs,
		};
		const saved = await mutate(
			() =>
				draft.id
					? updateTenantAiSkillAction(tenantId, draft.id, input)
					: createTenantAiSkillAction(tenantId, input),
			draft.id ? 'Action updated.' : 'Draft action created.',
		);
		if (!saved) return;
		setActionDraft(blankAction(settings?.connections[0]?.id));
		setShowActionForm(false);
	}

	function editAction(action: AiSkillAction): void {
		setActionDraft({
			connectionId: action.connectionId,
			...(action.credentialProfileId
				? { credentialProfileId: action.credentialProfileId }
				: {}),
			description: action.description,
			id: action.id,
			inputs: action.inputs,
			maxResponseBytes: action.maxResponseBytes,
			method: action.method,
			name: action.name,
			notFoundStatuses: action.notFoundStatuses,
			outputs: action.outputs,
			path: action.path,
			readOnlyAttested: action.readOnlyAttested,
			status: action.status === 'disabled' ? 'disabled' : 'draft',
			timeoutMs: action.timeoutMs,
		});
		setShowActionForm(true);
	}

	function updateInput(index: number, change: Partial<AiSkillInputDefinition>): void {
		setActionDraft((current) => ({
			...current,
			inputs: current.inputs.map((definition, currentIndex) =>
				currentIndex === index ? { ...definition, ...change } : definition,
			),
		}));
	}

	function updateOutput(index: number, change: Partial<AiSkillOutputMapping>): void {
		setActionDraft((current) => ({
			...current,
			outputs: current.outputs.map((mapping, currentIndex) =>
				currentIndex === index ? { ...mapping, ...change } : mapping,
			),
		}));
	}

	function beginTest(action: AiSkillAction): void {
		setTestingAction(action);
		setTestResult(undefined);
		setTestValues(
			Object.fromEntries(
				action.inputs.map((input) => [input.key, input.type === 'boolean' ? false : '']),
			),
		);
	}

	async function runTest(event: FormEvent<HTMLFormElement>): Promise<void> {
		event.preventDefault();
		if (!testingAction || busy) return;
		if (
			!window.confirm(
				'This test will make a real read-only request to the configured external service. Continue?',
			)
		) {
			return;
		}
		const parameters: Record<string, unknown> = {};
		for (const input of testingAction.inputs) {
			const value = testValues[input.key];
			if (value === '' || value === undefined) continue;
			parameters[input.key] =
				input.type === 'integer' || input.type === 'decimal' ? Number(value) : value;
		}
		setBusy(true);
		setError(undefined);
		setTestResult(undefined);
		try {
			setTestResult(
				await testTenantAiSkillAction(tenantId, testingAction.id, { parameters }),
			);
		} catch (caught) {
			setError(caught instanceof Error ? caught.message : 'Could not test the action');
		} finally {
			setBusy(false);
		}
	}

	if (loading) {
		return <div className="empty-configuration">Loading Skills…</div>;
	}

	return (
		<section className="tenant-ai-skills-section">
			<div className="tenant-ai-skills-heading">
				<div>
					<h4>Skills</h4>
					<p>
						Configure customer-owned API connections and read-only Draft actions. Manage
						AI agent permissions and live availability in Agent Actions.
					</p>
				</div>
			</div>

			{error && <div className="alert error">{error}</div>}
			{message && <div className="alert success">{message}</div>}
			<div className="ai-skills-foundation-notice">
				<strong>Safe setup mode</strong>
				<span>
					Only an owner or admin can configure and deliberately test these Draft actions.
					Use Agent Actions to save immutable versions and assign them to AI agents, then
					publish from Overview &amp; Publish. Draft tests do not enable live execution.
				</span>
			</div>

			<section className="settings-card integration-panel ai-skills-panel">
				<div className="integration-panel-heading ai-skills-panel-heading">
					<div>
						<h4 className="panel-title">Connections</h4>
						<p>Each connection permits one public HTTPS API origin.</p>
					</div>
					<button
						className="secondary-button"
						onClick={() => {
							setConnectionDraft(blankConnection());
							setShowConnectionForm((current) => !current);
						}}
						type="button"
					>
						{showConnectionForm ? 'Cancel' : 'Add connection'}
					</button>
				</div>
				{showConnectionForm && (
					<form
						className="ai-skill-editor"
						onSubmit={(event) => void saveConnection(event)}
					>
						<label>
							<span>Name</span>
							<input
								maxLength={120}
								onChange={(event) =>
									updateConnectionDraft({ name: event.currentTarget.value })
								}
								required
								value={connectionDraft.name}
							/>
						</label>
						<label>
							<span>Public HTTPS origin</span>
							<input
								maxLength={2048}
								onChange={(event) =>
									updateConnectionDraft({ baseOrigin: event.currentTarget.value })
								}
								placeholder="https://api.example.com"
								required
								type="url"
								value={connectionDraft.baseOrigin}
							/>
						</label>
						<label className="ai-skill-wide-field">
							<span>Description</span>
							<textarea
								maxLength={1000}
								onChange={(event) =>
									updateConnectionDraft({
										description: event.currentTarget.value,
									})
								}
								rows={2}
								value={connectionDraft.description}
							/>
						</label>
						<button className="primary-button" disabled={busy}>
							{connectionDraft.id ? 'Save connection' : 'Create connection'}
						</button>
					</form>
				)}
				<div className="ai-skill-resource-list">
					{settings?.connections.map((connection) => (
						<article className="ai-skill-resource" key={connection.id}>
							<div>
								<strong>{connection.name}</strong>
								<span>{connection.baseOrigin}</span>
								{connection.description && <small>{connection.description}</small>}
							</div>
							<span className={'status-badge ' + connection.status}>
								{connection.status}
							</span>
							<div className="ai-skill-resource-actions">
								<button onClick={() => editConnection(connection)} type="button">
									Edit
								</button>
								<button
									disabled={busy}
									onClick={() =>
										void mutate(
											() =>
												setTenantAiSkillConnectionStatus(
													tenantId,
													connection.id,
													connection.status === 'active'
														? 'disabled'
														: 'active',
												),
											connection.status === 'active'
												? 'Connection disabled.'
												: 'Connection enabled.',
										)
									}
									type="button"
								>
									{connection.status === 'active' ? 'Disable' : 'Enable'}
								</button>
								<button
									className="danger-link"
									disabled={busy}
									onClick={() => {
										if (
											window.confirm(
												'Archive this connection and stop its tests?',
											)
										) {
											void mutate(
												() =>
													setTenantAiSkillConnectionStatus(
														tenantId,
														connection.id,
														'archived',
													),
												'Connection archived.',
											);
										}
									}}
									type="button"
								>
									Archive
								</button>
							</div>
						</article>
					))}
					{!settings?.connections.length && (
						<div className="empty-configuration">No API connections configured.</div>
					)}
				</div>
			</section>

			{testingAction && (
				<section className="settings-card integration-panel ai-skill-test-panel">
					<div className="integration-panel-heading ai-skills-panel-heading">
						<div>
							<h4 className="panel-title">Test {testingAction.name}</h4>
							<p>
								This uses the saved Draft and makes one real read-only request. Test
								values and returned values are not stored in evidence.
							</p>
						</div>
						<button
							className="secondary-button"
							onClick={() => {
								setTestingAction(undefined);
								setTestResult(undefined);
							}}
							type="button"
						>
							Close
						</button>
					</div>
					<form className="ai-skill-test-form" onSubmit={(event) => void runTest(event)}>
						{testingAction.inputs.map((input) => (
							<label key={input.key}>
								<span>{input.label}</span>
								{input.type === 'boolean' ? (
									<select
										onChange={(event) =>
											updateTestValue(
												input.key,
												event.currentTarget.value === 'true',
											)
										}
										value={String(testValues[input.key] ?? false)}
									>
										<option value="false">No</option>
										<option value="true">Yes</option>
									</select>
								) : input.type === 'enum' ? (
									<select
										onChange={(event) =>
											updateTestValue(input.key, event.currentTarget.value)
										}
										required={input.required}
										value={String(testValues[input.key] ?? '')}
									>
										<option value="">Select</option>
										{input.enumValues?.map((value) => (
											<option key={value} value={value}>
												{value}
											</option>
										))}
									</select>
								) : (
									<input
										onChange={(event) =>
											updateTestValue(input.key, event.currentTarget.value)
										}
										required={input.required}
										type={
											input.type === 'integer' || input.type === 'decimal'
												? 'number'
												: input.type === 'date'
													? 'date'
													: input.type === 'date_time'
														? 'datetime-local'
														: 'text'
										}
										value={String(testValues[input.key] ?? '')}
									/>
								)}
								{input.description && <small>{input.description}</small>}
							</label>
						))}
						<button className="primary-button" disabled={busy}>
							{busy ? 'Testing…' : 'Run real read-only test'}
						</button>
					</form>
					{testResult && (
						<div
							className={
								'ai-skill-test-result ' +
								(testResult.outcome === 'succeeded' ? 'success' : 'warning')
							}
						>
							<strong>
								{testResult.outcome === 'succeeded'
									? 'Test succeeded'
									: testResult.outcome === 'not_found'
										? 'No matching record was found'
										: 'Test failed safely'}
							</strong>
							<span>
								{testResult.httpStatus
									? 'HTTP ' + testResult.httpStatus + ' · '
									: ''}
								{testResult.durationMs} ms
								{testResult.errorCode ? ' · ' + testResult.errorCode : ''}
							</span>
							{testResult.fields && (
								<dl>
									{Object.entries(testResult.fields).map(([key, value]) => (
										<div key={key}>
											<dt>
												{testingAction.outputs.find(
													(output) => output.key === key,
												)?.label ?? key}
											</dt>
											<dd>
												{typeof value === 'object'
													? JSON.stringify(value)
													: String(value)}
											</dd>
										</div>
									))}
								</dl>
							)}
						</div>
					)}
				</section>
			)}

			<section className="settings-card integration-panel ai-skills-panel">
				<div className="integration-panel-heading ai-skills-panel-heading">
					<div>
						<h4 className="panel-title">Actions</h4>
						<p>
							Define each approved lookup, the information it needs and the safe
							response fields it can return.
						</p>
					</div>
					<button
						className="secondary-button"
						disabled={!settings?.connections.length}
						onClick={() => {
							setActionDraft(blankAction(settings?.connections[0]?.id));
							setShowActionForm((current) => !current);
						}}
						type="button"
					>
						{showActionForm ? 'Cancel' : 'Add action'}
					</button>
				</div>

				{showActionForm && (
					<form
						className="ai-skill-action-editor"
						onSubmit={(event) => void saveAction(event)}
					>
						<div className="ai-skill-editor">
							<label>
								<span>Action name</span>
								<input
									maxLength={120}
									onChange={(event) =>
										updateActionDraft({ name: event.currentTarget.value })
									}
									placeholder="Track parcel"
									required
									value={actionDraft.name}
								/>
							</label>
							<label>
								<span>Connection</span>
								<select
									onChange={(event) =>
										updateActionDraft({
											connectionId: event.currentTarget.value,
											credentialProfileId: undefined,
										})
									}
									required
									value={actionDraft.connectionId}
								>
									<option value="">Select connection</option>
									{settings?.connections.map((connection) => (
										<option key={connection.id} value={connection.id}>
											{connection.name}
										</option>
									))}
								</select>
							</label>
							<label>
								<span>Credential</span>
								<select
									onChange={(event) =>
										updateActionDraft({
											credentialProfileId:
												event.currentTarget.value || undefined,
										})
									}
									value={actionDraft.credentialProfileId ?? ''}
								>
									<option value="">No authentication</option>
									{credentialsForAction.map((credential) => (
										<option key={credential.id} value={credential.id}>
											{credential.name}
										</option>
									))}
								</select>
							</label>
							<label>
								<span>Method</span>
								<select
									onChange={(event) =>
										updateActionDraft({
											method: event.currentTarget.value as 'GET' | 'POST',
											readOnlyAttested: false,
										})
									}
									value={actionDraft.method}
								>
									<option value="GET">GET</option>
									<option value="POST">POST — confirmed read-only</option>
								</select>
							</label>
							{actionDraft.method === 'POST' && (
								<label className="ai-skill-checkbox ai-skill-wide-field">
									<input
										checked={actionDraft.readOnlyAttested}
										onChange={(event) =>
											updateActionDraft({
												readOnlyAttested: event.currentTarget.checked,
											})
										}
										required
										type="checkbox"
									/>
									<span>
										I confirm this POST action only reads data and cannot change
										the customer system.
									</span>
								</label>
							)}
							<label className="ai-skill-wide-field">
								<span>When should the AI use this action?</span>
								<textarea
									maxLength={1000}
									onChange={(event) =>
										updateActionDraft({
											description: event.currentTarget.value,
										})
									}
									placeholder="Use when a visitor wants the latest tracking status for a parcel."
									required
									rows={2}
									value={actionDraft.description}
								/>
							</label>
							<label className="ai-skill-wide-field">
								<span>Relative API path</span>
								<input
									maxLength={1000}
									onChange={(event) =>
										updateActionDraft({ path: event.currentTarget.value })
									}
									placeholder="/parcels/{reference}"
									required
									value={actionDraft.path}
								/>
								<small>
									Use a placeholder matching each Path input, such as{' '}
									{'{reference}'}.
								</small>
							</label>
						</div>

						<fieldset className="ai-skill-definition-builder">
							<div className="ai-skill-builder-heading">
								<div>
									<legend>Information the action needs</legend>
									<p>The AI will ask for any required value that is missing.</p>
								</div>
								<button
									className="secondary-button"
									onClick={() =>
										setActionDraft((current) => ({
											...current,
											inputs: [...current.inputs, blankInput()],
										}))
									}
									type="button"
								>
									Add input
								</button>
							</div>
							{actionDraft.inputs.map((definition, index) => (
								<div className="ai-skill-definition-row" key={'input-' + index}>
									<label>
										<span>Label</span>
										<input
											maxLength={120}
											onChange={(event) =>
												updateInput(index, {
													label: event.currentTarget.value,
												})
											}
											required
											value={definition.label}
										/>
									</label>
									<label>
										<span>Key</span>
										<input
											maxLength={64}
											onChange={(event) => {
												const previous = definition.key;
												const key = event.currentTarget.value;
												updateInput(index, {
													key,
													target:
														definition.target === previous
															? key
															: definition.target,
												});
											}}
											required
											value={definition.key}
										/>
									</label>
									<label>
										<span>Type</span>
										<select
											onChange={(event) =>
												updateInput(index, {
													type: event.currentTarget
														.value as AiSkillValueType,
												})
											}
											value={definition.type}
										>
											{valueTypes.map((type) => (
												<option key={type.value} value={type.value}>
													{type.label}
												</option>
											))}
										</select>
									</label>
									<label>
										<span>Send in</span>
										<select
											onChange={(event) =>
												updateInput(index, {
													location: event.currentTarget
														.value as AiSkillInputDefinition['location'],
												})
											}
											value={definition.location}
										>
											<option value="path">Path</option>
											<option value="query">Query parameter</option>
											<option value="header">Header</option>
											<option
												disabled={actionDraft.method === 'GET'}
												value="body"
											>
												JSON body
											</option>
										</select>
									</label>
									<label>
										<span>API field / placeholder</span>
										<input
											maxLength={120}
											onChange={(event) =>
												updateInput(index, {
													target: event.currentTarget.value,
												})
											}
											required
											value={definition.target}
										/>
									</label>
									{definition.type === 'enum' && (
										<label className="ai-skill-wide-field">
											<span>Choices, separated by commas</span>
											<input
												onChange={(event) =>
													updateInput(index, {
														enumValues:
															event.currentTarget.value.split(','),
													})
												}
												required
												value={(definition.enumValues ?? []).join(', ')}
											/>
										</label>
									)}
									<label className="ai-skill-checkbox">
										<input
											checked={definition.required}
											onChange={(event) =>
												updateInput(index, {
													required: event.currentTarget.checked,
												})
											}
											type="checkbox"
										/>
										<span>Required</span>
									</label>
									<button
										className="danger-link ai-skill-remove-row"
										onClick={() =>
											setActionDraft((current) => ({
												...current,
												inputs: current.inputs.filter(
													(_input, currentIndex) =>
														currentIndex !== index,
												),
											}))
										}
										type="button"
									>
										Remove input
									</button>
								</div>
							))}
							{!actionDraft.inputs.length && (
								<div className="empty-configuration">
									No visitor inputs required.
								</div>
							)}
						</fieldset>

						<fieldset className="ai-skill-definition-builder">
							<div className="ai-skill-builder-heading">
								<div>
									<legend>Safe response fields</legend>
									<p>
										Only these mapped fields can be shown to an AI or Copilot.
									</p>
								</div>
								<button
									className="secondary-button"
									onClick={() =>
										setActionDraft((current) => ({
											...current,
											outputs: [...current.outputs, blankOutput()],
										}))
									}
									type="button"
								>
									Add response field
								</button>
							</div>
							{actionDraft.outputs.map((mapping, index) => (
								<div className="ai-skill-definition-row" key={'output-' + index}>
									<label>
										<span>Label</span>
										<input
											maxLength={120}
											onChange={(event) =>
												updateOutput(index, {
													label: event.currentTarget.value,
												})
											}
											required
											value={mapping.label}
										/>
									</label>
									<label>
										<span>Key</span>
										<input
											maxLength={64}
											onChange={(event) =>
												updateOutput(index, {
													key: event.currentTarget.value,
												})
											}
											required
											value={mapping.key}
										/>
									</label>
									<label>
										<span>JSON response path</span>
										<input
											maxLength={500}
											onChange={(event) =>
												updateOutput(index, {
													path: event.currentTarget.value,
												})
											}
											placeholder="parcel.status"
											required
											value={mapping.path}
										/>
									</label>
									<label>
										<span>Type</span>
										<select
											onChange={(event) =>
												updateOutput(index, {
													type: event.currentTarget
														.value as AiSkillValueType,
												})
											}
											value={mapping.type}
										>
											{valueTypes.map((type) => (
												<option key={type.value} value={type.value}>
													{type.label}
												</option>
											))}
										</select>
									</label>
									<label>
										<span>May be used by</span>
										<select
											onChange={(event) =>
												updateOutput(index, {
													visibility: event.currentTarget
														.value as AiSkillOutputMapping['visibility'],
												})
											}
											value={mapping.visibility}
										>
											<option value="visitor">AI reply to visitor</option>
											<option value="copilot">Copilot only</option>
											<option value="internal">Internal status only</option>
										</select>
									</label>
									<button
										className="danger-link ai-skill-remove-row"
										onClick={() =>
											setActionDraft((current) => ({
												...current,
												outputs: current.outputs.filter(
													(_output, currentIndex) =>
														currentIndex !== index,
												),
											}))
										}
										type="button"
									>
										Remove field
									</button>
								</div>
							))}
							{!actionDraft.outputs.length && (
								<div className="empty-configuration">
									Add at least one response field before testing this action.
								</div>
							)}
						</fieldset>

						<details className="ai-skill-advanced">
							<summary>Safety limits and not-found response</summary>
							<div className="ai-skill-editor">
								<label>
									<span>Timeout (milliseconds)</span>
									<input
										max={15000}
										min={1000}
										onChange={(event) =>
											updateActionDraft({
												timeoutMs: Number(event.currentTarget.value),
											})
										}
										required
										type="number"
										value={actionDraft.timeoutMs}
									/>
								</label>
								<label>
									<span>Maximum response size (bytes)</span>
									<input
										max={262144}
										min={1024}
										onChange={(event) =>
											updateActionDraft({
												maxResponseBytes: Number(event.currentTarget.value),
											})
										}
										required
										type="number"
										value={actionDraft.maxResponseBytes}
									/>
								</label>
								<label>
									<span>Not-found HTTP statuses</span>
									<input
										onChange={(event) =>
											updateActionDraft({
												notFoundStatuses: event.currentTarget.value
													.split(',')
													.map((value) => Number(value.trim()))
													.filter(Number.isInteger),
											})
										}
										placeholder="404"
										value={actionDraft.notFoundStatuses.join(', ')}
									/>
								</label>
							</div>
						</details>
						<div className="ai-skill-form-actions">
							<button
								className="primary-button"
								disabled={
									busy ||
									!actionDraft.outputs.length ||
									(actionDraft.method === 'POST' && !actionDraft.readOnlyAttested)
								}
							>
								{actionDraft.id ? 'Save Draft action' : 'Create Draft action'}
							</button>
							<button
								className="secondary-button"
								onClick={() => setShowActionForm(false)}
								type="button"
							>
								Cancel
							</button>
						</div>
					</form>
				)}

				<div className="ai-skill-resource-list">
					{settings?.actions.map((action) => {
						const connection = settings.connections.find(
							(item) => item.id === action.connectionId,
						);
						return (
							<article
								className="ai-skill-resource ai-skill-action-resource"
								key={action.id}
							>
								<div>
									<strong>{action.name}</strong>
									<span>
										{connection?.name ?? 'Unknown connection'} · {action.method}{' '}
										{action.path}
									</span>
									<small>{action.description}</small>
								</div>
								<span className={'status-badge ' + action.status}>
									{action.status}
								</span>
								<div className="ai-skill-resource-actions">
									<button onClick={() => editAction(action)} type="button">
										Edit
									</button>
									<button
										disabled={busy || action.status !== 'draft'}
										onClick={() => beginTest(action)}
										type="button"
									>
										Test
									</button>
									<button
										disabled={busy}
										onClick={() =>
											void mutate(
												() =>
													setTenantAiSkillActionStatus(
														tenantId,
														action.id,
														action.status === 'draft'
															? 'disabled'
															: 'draft',
													),
												action.status === 'draft'
													? 'Action disabled.'
													: 'Action returned to Draft.',
											)
										}
										type="button"
									>
										{action.status === 'draft' ? 'Disable' : 'Enable Draft'}
									</button>
									<button
										className="danger-link"
										disabled={busy}
										onClick={() => {
											if (window.confirm('Archive this Draft action?')) {
												void mutate(
													() =>
														setTenantAiSkillActionStatus(
															tenantId,
															action.id,
															'archived',
														),
													'Action archived.',
												);
											}
										}}
										type="button"
									>
										Archive
									</button>
								</div>
							</article>
						);
					})}
					{!settings?.actions.length && (
						<div className="empty-configuration">No Draft actions configured.</div>
					)}
				</div>
			</section>

			<section className="settings-card integration-panel ai-skills-panel">
				<div className="integration-panel-heading ai-skills-panel-heading">
					<div>
						<h4 className="panel-title">Credentials</h4>
						<p>Secrets are encrypted, write-only and never shown again after saving.</p>
					</div>
					<button
						className="secondary-button"
						disabled={!settings?.connections.length}
						onClick={() => {
							setCredentialDraft(blankCredential(settings?.connections[0]?.id));
							setShowCredentialForm((current) => !current);
						}}
						type="button"
					>
						{showCredentialForm ? 'Cancel' : 'Add credential'}
					</button>
				</div>
				{showCredentialForm && (
					<form
						className="ai-skill-editor"
						onSubmit={(event) => void saveCredential(event)}
					>
						<label>
							<span>Connection</span>
							<select
								disabled={!!credentialDraft.id}
								onChange={(event) =>
									updateCredentialDraft({
										connectionId: event.currentTarget.value,
									})
								}
								required
								value={credentialDraft.connectionId}
							>
								<option value="">Select connection</option>
								{settings?.connections.map((connection) => (
									<option key={connection.id} value={connection.id}>
										{connection.name}
									</option>
								))}
							</select>
						</label>
						<label>
							<span>Credential name</span>
							<input
								disabled={!!credentialDraft.id}
								maxLength={120}
								onChange={(event) =>
									updateCredentialDraft({ name: event.currentTarget.value })
								}
								required
								value={credentialDraft.name}
							/>
						</label>
						<label>
							<span>Authentication</span>
							<select
								disabled={!!credentialDraft.id}
								onChange={(event) =>
									updateCredentialDraft({
										type: event.currentTarget.value as AiSkillCredentialType,
									})
								}
								value={credentialDraft.type}
							>
								<option value="none">No authentication</option>
								<option value="bearer">Bearer token</option>
								<option value="api_key_header">API key header</option>
								<option value="basic">Basic username/password</option>
							</select>
						</label>
						{credentialDraft.type === 'api_key_header' && (
							<label>
								<span>Header name</span>
								<input
									maxLength={120}
									onChange={(event) =>
										updateCredentialDraft({
											headerName: event.currentTarget.value,
										})
									}
									required
									value={credentialDraft.headerName}
								/>
							</label>
						)}
						{credentialDraft.type === 'basic' && (
							<label>
								<span>
									{credentialDraft.id
										? 'Username (re-enter in full)'
										: 'Username'}
								</span>
								<input
									autoComplete="off"
									maxLength={256}
									onChange={(event) =>
										updateCredentialDraft({
											username: event.currentTarget.value,
										})
									}
									required
									value={credentialDraft.username}
								/>
							</label>
						)}
						{credentialDraft.type !== 'none' &&
							(credentialDraft.type === 'basic' ? (
								<label>
									<span>Password</span>
									<input
										autoComplete="new-password"
										onChange={(event) =>
											updateCredentialDraft({
												password: event.currentTarget.value,
											})
										}
										required
										type="password"
										value={credentialDraft.password}
									/>
								</label>
							) : (
								<label>
									<span>
										{credentialDraft.type === 'bearer' ? 'Token' : 'API key'}
									</span>
									<input
										autoComplete="new-password"
										onChange={(event) =>
											updateCredentialDraft({
												secret: event.currentTarget.value,
											})
										}
										required
										type="password"
										value={credentialDraft.secret}
									/>
								</label>
							))}
						<button className="primary-button" disabled={busy}>
							{credentialDraft.id ? 'Replace credential' : 'Save credential'}
						</button>
					</form>
				)}
				<div className="ai-skill-resource-list">
					{settings?.credentials.map((credential) => {
						const connection = settings.connections.find(
							(item) => item.id === credential.connectionId,
						);
						return (
							<article className="ai-skill-resource" key={credential.id}>
								<div>
									<strong>{credential.name}</strong>
									<span>
										{connection?.name ?? 'Unknown connection'} ·{' '}
										{credentialTypeLabel(credential.type)}
									</span>
									{credential.hint && <small>{credential.hint}</small>}
								</div>
								<span className={'status-badge ' + credential.status}>
									{credential.status}
								</span>
								<div className="ai-skill-resource-actions">
									<button
										disabled={credential.type === 'none'}
										onClick={() => replaceCredential(credential)}
										type="button"
									>
										Replace
									</button>
									<button
										disabled={busy}
										onClick={() =>
											void mutate(
												() =>
													setTenantAiSkillCredentialStatus(
														tenantId,
														credential.id,
														credential.status === 'active'
															? 'disabled'
															: 'active',
													),
												credential.status === 'active'
													? 'Credential disabled.'
													: 'Credential enabled.',
											)
										}
										type="button"
									>
										{credential.status === 'active' ? 'Disable' : 'Enable'}
									</button>
									<button
										className="danger-link"
										disabled={busy}
										onClick={() => {
											if (window.confirm('Archive this credential?')) {
												void mutate(
													() =>
														setTenantAiSkillCredentialStatus(
															tenantId,
															credential.id,
															'archived',
														),
													'Credential archived.',
												);
											}
										}}
										type="button"
									>
										Archive
									</button>
								</div>
							</article>
						);
					})}
					{!settings?.credentials.length && (
						<div className="empty-configuration">No credentials configured.</div>
					)}
				</div>
			</section>
		</section>
	);
}
